import React, { useMemo, useState } from 'react';
import { TBaseTagContext } from '../../types';
import { TCustomFormContext, TInputState, TTag } from './types';
import { validate } from '@krugom/validator';
import { useHandleCleanup } from './hooks/useHandleCleanup';
import { useHandleReset } from './hooks/useHandleReset';
import { useHandleSetInput } from './hooks/useHandleSetInput';
import { useHandleSetValue } from './hooks/useHandleSetValue';
import { isFunction } from 'lodash';
import useContexts from '../../../hooks/useContexts';
import { useHandleSubmit } from './hooks/useHandleSubmit';
import { constructSubmitData } from './helpers/constructSubmitData';
import { useHandleDelInput } from './hooks/useHandleDelInput';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { TValidationScheme } from '@krugom/validator/dist/types';

const validationScheme: TValidationScheme = {
  type: 'object',
  properties: {
    onSubmit: {
      type: 'custom',
      validator: (v: unknown) => isFunction(v),
    },
  },
};

export const CustomForm: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const [inputs, setInputs] = useState<Record<string, TInputState>>({});

  const result = validate(tagProps, validationScheme);

  const handleCleanup = useHandleCleanup({
    inputs,
    setInputs,
  });

  const handleReset = useHandleReset({
    inputs,
    setInputs,
  });

  const handleSetInput = useHandleSetInput({
    inputs,
    setInputs,
  });

  const handleSetValue = useHandleSetValue({
    inputs,
    setInputs,
  });

  const handleDelInput = useHandleDelInput({
    inputs,
    setInputs,
  });

  const selectorContexts = useContexts(tagProps.schema?.contextItems || []);
  const submitData = useMemo(() => constructSubmitData(inputs), [inputs]);

  const handleSubmit = useHandleSubmit({
    inputs,
    setInputs,
    context: {
      ...selectorContexts,
      ...context,
      form: { submitData, inputs } as TCustomFormContext,
    },
  });

  const form: TCustomFormContext = useMemo(
    () => ({
      submitData,
      inputs,
      handleCleanup,
      handleReset,
      handleSetInput,
      handleSetValue,
      handleSubmit,
      handleDelInput,
    }),
    [inputs, submitData, handleCleanup, handleReset, handleSetInput, handleSetValue, handleSubmit, handleDelInput]
  );

  const childrenContext: Partial<TBaseTagContext> = useMemo(
    () => ({
      ...context,
      form,
    }),
    [context, form]
  );

  if (!result.isValid) {
    console.error(
      'Тэг: $FormContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  return <UniversalChildren context={childrenContext}>{tagProps.children}</UniversalChildren>;
});
CustomForm.displayName = 'CustomForm';
